export type MarketingWidgetChannel =
  | {
      type: 'WHATSAPP';
      label: string;
      value: string;
      prefilledMessage: string;
    }
  | {
      type: 'WEB_CHAT' | 'EMAIL';
      label: string;
      value: string;
    };

export interface MarketingWidgetConfig {
  tenantId: string;
  apiUrl: string;
  /** Widget UI language; mirrors the page locale so the embed is explicit. */
  locale: 'en' | 'ar';
  /**
   * Per-embed greeting override (read by the widget as `window.__kasbly.greeting`).
   * Set on the Arabic site so the opening line matches the page language even
   * when the tenant's server-configured greeting is English.
   */
  greeting?: string;
  widget: {
    enabled: true;
    greeting: string;
    position: 'bottom-right';
    showBranding: true;
    theme: {
      primaryColor: string;
    };
    channels: MarketingWidgetChannel[];
    tracking: {
      utmSource: string;
      utmCampaign: string;
    };
  };
}

export interface WidgetConfigEnv {
  [key: string]: string | undefined;
  NEXT_PUBLIC_API_URL?: string;
  NEXT_PUBLIC_WIDGET_BASE_URL?: string;
  NEXT_PUBLIC_WIDGET_ENABLED?: string;
  NEXT_PUBLIC_WIDGET_TENANT_ID?: string;
}

const DEFAULT_API_URL = 'https://api.kasbly.com';
const DEFAULT_WIDGET_BASE_URL = 'https://kasbly.com';
const STAGING_WIDGET_BASE_URL = 'https://staging.kasbly.com';

/**
 * Escapes JSON for safe embedding inside an HTML <script> tag via innerHTML.
 * JSON.stringify handles string escaping; this prevents breaking out of the
 * script context with markup such as `</script>`.
 */
export function escapeScriptJson(json: string): string {
  return json.replace(/</g, '\\u003c').replace(/>/g, '\\u003e');
}

export function resolveWidgetBaseUrl(env: WidgetConfigEnv): string {
  if (env.NEXT_PUBLIC_WIDGET_BASE_URL) {
    return env.NEXT_PUBLIC_WIDGET_BASE_URL;
  }

  const apiUrl = env.NEXT_PUBLIC_API_URL ?? '';
  if (apiUrl.includes('staging')) {
    return STAGING_WIDGET_BASE_URL;
  }

  return DEFAULT_WIDGET_BASE_URL;
}

export function buildMarketingWidgetConfig(
  env: WidgetConfigEnv,
  locale: 'en' | 'ar' = 'en',
): MarketingWidgetConfig | null {
  if (env.NEXT_PUBLIC_WIDGET_ENABLED !== 'true' || !env.NEXT_PUBLIC_WIDGET_TENANT_ID) {
    return null;
  }

  return {
    tenantId: env.NEXT_PUBLIC_WIDGET_TENANT_ID,
    apiUrl: env.NEXT_PUBLIC_API_URL ?? DEFAULT_API_URL,
    locale,
    // Arabic pages get an Arabic opening line regardless of the tenant's
    // server-configured (English) greeting; English pages keep the server one.
    ...(locale === 'ar' ? { greeting: 'أهلاً بك! اسألني أي شيء عن كسبلي.' } : {}),
    widget: {
      enabled: true,
      greeting: 'Hi! How can we help you today?',
      position: 'bottom-right',
      showBranding: true,
      theme: {
        primaryColor: '#4F46E5',
      },
      channels: [
        {
          type: 'WHATSAPP',
          label: 'WhatsApp',
          value: '',
          prefilledMessage: 'Hi! I found you on your website.',
        },
        {
          type: 'WEB_CHAT',
          label: 'Live Chat',
          value: '',
        },
        {
          type: 'EMAIL',
          label: 'Email',
          value: '',
        },
      ],
      tracking: {
        utmSource: 'marketing-website',
        utmCampaign: '',
      },
    },
  };
}
